// Week arithmetic and the reduced activity vocabulary shared by the weekly
// planner and its Excel export.
//
// The daily modal writes the full set of prod_employee_schedule activities and
// can stack several of them on one shift. The weekly planner is deliberately
// limited to a single activity per employee per day: it exists to hand out next
// month's schedule, not to correct hours after the fact.

import { dayKey } from '@/helpers/dates'

export const LABEL_PREFIX = 'pages.production_data.employee_schedule'

const PREFIX = LABEL_PREFIX

export const WEEK_SHIFTS = ['AM', 'PM', 'Night']

// The printed schedule gathers every absence in one block instead of splitting
// them across the shifts they happen to be recorded on.
export const ABSENCE_GROUP = 'absence'

// Nothing should be stored without a shift, but a row that is has to stay visible.
const NO_SHIFT = 'other'

export const WEEK_ACTIVITIES = [
  { key: 'washing', labelKey: PREFIX + '.activities.washing' },
  { key: 'machineWash', labelKey: PREFIX + '.activities.machineWash' },
  { key: 'repair', labelKey: PREFIX + '.activities.repair' },
  { key: 'repair__door', labelKey: PREFIX + '.activities.repair__door' },
  { key: 'forklift', labelKey: PREFIX + '.activities.forklift' },
  { key: 'special', labelKey: PREFIX + '.activities.special' },
  { key: 'absent__AL', labelKey: PREFIX + '.activities.absent__AL', absence: true },
  { key: 'absent__ML', labelKey: PREFIX + '.activities.absent__ML', absence: true },
  { key: 'absent__AB', labelKey: PREFIX + '.activities.absent__AB', absence: true }
]

// The employee.lastShift payload names its activities differently from the
// values actually stored on a schedule row.
const LAST_SHIFT_ACTIVITIES = [
  { field: 'washing', activity: 'washing' },
  { field: 'machine', activity: 'machineWash' },
  { field: 'repair', activity: 'repair' },
  { field: 'doors', activity: 'repair__door' },
  { field: 'forklift', activity: 'forklift' },
  { field: 'special', activity: 'special' }
]

export const DEFAULT_PATTERN = { shift: 'AM', activity: 'washing', hours: 8 }

// Our locale files are keyed on codes Intl does not always know ('cn', 'kr'), so
// weekday and month names are asked for with a real tag.
const DATE_LOCALES = { cn: 'zh-CN', kr: 'ko-KR', en: 'en-GB' }

export function dateLocale (locale) {
  return DATE_LOCALES[locale] || locale || 'en-GB'
}

export function isAbsenceActivity (activity) {
  return String(activity).split('__')[0] === 'absent'
}

// 'YYYY-MM-DD' in the browser's timezone, the key the whole page compares dates on.
export function dateKey (date) {
  return date.toLocaleDateString('en-CA')
}

export function startOfWeek (date) {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7))
  return monday
}

export function addDays (date, days) {
  const shifted = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  shifted.setDate(shifted.getDate() + days)
  return shifted
}

export function weekDates (weekStart) {
  return Array.from({ length: 7 }, (unused, day) => addDays(weekStart, day))
}

// ISO 8601: the week owning this date is the one holding its Thursday.
export function isoWeekNumber (date) {
  const thursday = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  thursday.setDate(thursday.getDate() + 3 - ((thursday.getDay() + 6) % 7))
  const yearStart = new Date(thursday.getFullYear(), 0, 1)
  return Math.ceil((((thursday - yearStart) / 86400000) + 1) / 7)
}

export function emptyCell () {
  return { shift: null, activity: null, hours: 0, entries: 0 }
}

export function roundHours (hours) {
  return Math.round((parseFloat(hours) || 0) * 100) / 100
}

// Hours actually worked: a day off or an absence contributes nothing.
export function cellHours (cell) {
  if (!cell.shift || isAbsenceActivity(cell.activity)) {
    return 0
  }
  return roundHours(cell.hours)
}

// Only the days the manager actually changed are written back, so a week can be
// re-opened and adjusted without flattening the corrections made in the daily form.
export function isCellDirty (cell) {
  const shift = cell.shift || null
  if (shift !== cell.original.shift) {
    return true
  }
  if (!shift) {
    return false
  }
  return cell.activity !== cell.original.activity || cellHours(cell) !== roundHours(cell.original.hours)
}

// A worked day with no hours would land in the daily table as a shift that
// declared nothing, so it has to be filled in or cleared before saving.
export function isCellIncomplete (cell) {
  return Boolean(cell.shift) && !isAbsenceActivity(cell.activity) && cellHours(cell) <= 0
}

export function employeeName (employee) {
  return employee.name + (employee.surname && employee.surname.length > 0 ? ' ' + employee.surname : '')
}

export function employeeSortKey (employee) {
  return ((employee.surname || '') + (employee.name || '')).toLowerCase()
}

// An employee only belongs to a week if they were on staff for part of it.
export function worksDuringWeek (employee, dates) {
  const first = dateKey(dates[0])
  const last = dateKey(dates[dates.length - 1])
  const entered = employee.date_entree ? dayKey(employee.date_entree) : null
  const left = employee.date_sortie ? dayKey(employee.date_sortie) : null
  return (!entered || entered <= last) && (!left || left >= first)
}

// Collapses the week's schedule rows into one cell per employee and day. A day
// holding several activities keeps the total hours and the dominant activity,
// and reports how many rows it came from so the caller can flag it.
export function buildWeekCells (schedules, dates) {
  const keys = dates.map(dateKey)
  const cellsByEmployee = new Map()
  schedules.forEach((entry) => {
    if (entry.activity === 'comment') {
      return
    }
    const day = keys.indexOf(dayKey(entry.date))
    if (day === -1) {
      return
    }
    if (!cellsByEmployee.has(entry.id_employee)) {
      cellsByEmployee.set(entry.id_employee, keys.map(() => emptyCell()))
    }
    const cell = cellsByEmployee.get(entry.id_employee)[day]
    const hours = parseFloat(entry.hours) || 0
    // Absences carry no hours, so the activity that actually filled the shift wins.
    if (cell.entries === 0 || hours > cell.hours) {
      cell.shift = entry.shift
      cell.activity = entry.activity
    }
    cell.entries += 1
    cell.hours = Math.round((cell.hours + hours) * 100) / 100
  })
  return cellsByEmployee
}

function activityOrder (activity) {
  const known = WEEK_ACTIVITIES.findIndex((candidate) => candidate.key === activity)
  return known === -1 ? WEEK_ACTIVITIES.length : known
}

function groupOrder (group) {
  if (group.absence) {
    return WEEK_SHIFTS.length + 1
  }
  const known = WEEK_SHIFTS.indexOf(group.key)
  return known === -1 ? WEEK_SHIFTS.length : known
}

// Activities the planner does not know about still have to print, under their
// stored value.
function activityLabels (activity) {
  const known = WEEK_ACTIVITIES.find((candidate) => candidate.key === activity)
  return { labelKey: known ? known.labelKey : null, label: known ? null : activity }
}

// Groups the week's schedule rows by shift and activity, each day holding the
// employees assigned to it. Unlike buildWeekCells this keeps every entry, so an
// employee who splits a shift between two activities appears in both rows —
// several sites use that to share productivity, and the printed schedule is
// expected to show it.
export function buildShiftActivityRows (schedules, dates, employees) {
  const keys = dates.map(dateKey)
  const staff = new Map((employees || []).map((employee) => [employee.id, employee]))
  const groups = new Map()

  schedules.forEach((entry) => {
    if (entry.activity === 'comment') {
      return
    }
    const day = keys.indexOf(dayKey(entry.date))
    if (day === -1) {
      return
    }
    const absence = isAbsenceActivity(entry.activity)
    const groupKey = absence ? ABSENCE_GROUP : (entry.shift || NO_SHIFT)
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        absence,
        labelKey: absence ? LABEL_PREFIX + '.week.absences' : null,
        label: absence ? null : (entry.shift || '—'),
        rows: new Map()
      })
    }
    const group = groups.get(groupKey)
    if (!group.rows.has(entry.activity)) {
      group.rows.set(entry.activity, {
        activity: entry.activity,
        absence,
        ...activityLabels(entry.activity),
        days: keys.map(() => ({ people: [], hours: 0 })),
        total: 0
      })
    }
    const row = group.rows.get(entry.activity)
    const cell = row.days[day]
    // An absence carries no hours, and neither should the totals it lands in.
    const hours = absence ? 0 : roundHours(entry.hours)
    const employee = staff.get(entry.id_employee)
    const already = cell.people.find((person) => person.id === entry.id_employee)
    if (already) {
      already.hours = roundHours(already.hours + hours)
    } else {
      cell.people.push({
        id: entry.id_employee,
        name: employee ? employeeName(employee) : '#' + entry.id_employee,
        sortKey: employee ? employeeSortKey(employee) : String(entry.id_employee),
        hours
      })
    }
    cell.hours = roundHours(cell.hours + hours)
    row.total = roundHours(row.total + hours)
  })

  return [...groups.values()]
    .sort((a, b) => groupOrder(a) - groupOrder(b) || String(a.key).localeCompare(String(b.key)))
    .map((group) => {
      return {
        ...group,
        rows: [...group.rows.values()]
          .sort((a, b) => activityOrder(a.activity) - activityOrder(b.activity) || a.activity.localeCompare(b.activity))
          .map((row) => {
            return {
              ...row,
              days: row.days.map((cell) => {
                return { ...cell, people: [...cell.people].sort((a, b) => a.sortKey.localeCompare(b.sortKey)) }
              })
            }
          })
      }
    })
}

// Seeds a new row from the last day the employee actually worked, which is the
// pattern the manager is most likely to repeat.
export function patternFromLastShift (employee) {
  const lastShift = employee.lastShift || employee.last_shift
  let best = null
  WEEK_SHIFTS.forEach((shift) => {
    const worked = lastShift && lastShift[shift]
    if (!worked) {
      return
    }
    LAST_SHIFT_ACTIVITIES.forEach(({ field, activity }) => {
      const hours = parseFloat(worked[field]) || 0
      if (hours > 0 && (!best || hours > best.hours)) {
        best = { shift, activity, hours }
      }
    })
  })
  return best || { ...DEFAULT_PATTERN }
}
